import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../hooks/useAuth';
import usePermissions from '../hooks/usePermissions';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import SendMailModal from '../components/SendMailModal';

const Invoices = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [filters, setFilters] = useState({
    status: '',
    customerId: '',
    page: 1,
    limit: 10,
  });
  const [pdfModal, setPdfModal] = useState({ isOpen: false, url: null, filename: null });
  const [sendMailModal, setSendMailModal] = useState({ isOpen: false, invoiceId: null, customerEmail: '', customerName: '' });

  // Fetch customers for filter
  const { data: customers } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    },
  });

  // Fetch invoices
  const { data: invoiceData, isLoading } = useQuery({
    queryKey: ['invoices', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await api.get(`/invoices?${params.toString()}`);
      return response.data;
    },
  });


  // Delete invoice mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/invoices/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('invoices.deletedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      console.error('Invoice deletion error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('invoices.deletionFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        confirmButtonText: t('common.ok')
      });
    },
  });

  // Generate PDF mutation (using template filling)
  const pdfMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.post(`/invoices/${id}/pdf/fill-template`, {}, {
        responseType: 'blob',
      });
      return { data: response.data, filename: `invoice-${id}.pdf` };
    },
    onSuccess: ({ data, filename }) => {
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      setPdfModal({ isOpen: true, url, filename });
    },
    onError: (error) => {
      console.error('PDF generation error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('invoices.pdfGenerationFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        confirmButtonText: t('common.ok')
      });
    },
  });

  // Handle send mail
  const handleSendMail = (invoice) => {
    setSendMailModal({
      isOpen: true,
      invoiceId: invoice._id,
      customerEmail: invoice.customerId?.email || '',
      customerName: invoice.customerId?.name || ''
    });
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      unpaid: 'bg-orange-100 text-orange-800',
      void: 'bg-red-100 text-red-800',
    };
    return colors[status] || colors.draft;
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const closePdfModal = () => {
    if (pdfModal.url) {
      window.URL.revokeObjectURL(pdfModal.url);
    }
    setPdfModal({ isOpen: false, url: null, filename: null });
  };

  const downloadPdf = () => {
    if (pdfModal.url && pdfModal.filename) {
      const link = document.createElement('a');
      link.href = pdfModal.url;
      link.setAttribute('download', pdfModal.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const invoices = invoiceData?.invoices || [];
  const pagination = invoiceData?.pagination || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('invoices.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('invoices.description')}
          </p>
        </div>
        <Link to="/invoices/new" className="btn btn-primary">
          {t('invoices.createInvoice')}
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="label">{t('invoices.status')}</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input"
            >
              <option value="">{t('invoices.allStatuses')}</option>
              <option value="draft">{t('invoices.statuses.draft')}</option>
              <option value="sent">{t('invoices.statuses.sent')}</option>
              <option value="paid">{t('invoices.statuses.paid')}</option>
              <option value="unpaid">{t('invoices.statuses.unpaid')}</option>
              <option value="void">{t('invoices.statuses.void')}</option>
            </select>
          </div>
          <div>
            <label className="label">{t('invoices.customer')}</label>
            <select
              value={filters.customerId}
              onChange={(e) => handleFilterChange('customerId', e.target.value)}
              className="input"
            >
              <option value="">{t('invoices.allCustomers')}</option>
              {customers?.map((customer) => (
                <option key={customer._id} value={customer._id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">{t('invoices.perPage')}</label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              className="input"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Invoices List */}
      <div className="card">
        {invoices.length > 0 ? (
          <>
            <div className="overflow-hidden">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('invoices.invoiceNumber')}</th>
                    <th>{t('invoices.customer')}</th>
                    <th>{t('invoices.invoiceDate')}</th>
                    <th>{t('invoices.status')}</th>
                    <th className="text-right">{t('common.total')}</th>
                    <th className="text-right">{t('invoices.paid')}</th>
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice._id}>
                      <td>
                        <Link
                          to={`/invoices/${invoice._id}`}
                          className="font-medium text-primary-600 hover:text-primary-500"
                        >
                          {invoice.invoiceNumber}
                        </Link>
                      </td>
                      <td>{invoice.customerId?.name}</td>
                      <td>{dayjs(invoice.invoiceDate).format('DD/MM/YYYY')}</td>
                      <td>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(invoice.status)}`}>
                          {t(`invoices.statuses.${invoice.status}`)}
                        </span>
                      </td>
                      <td className="text-right font-medium">
                        {formatCurrency(invoice.totals.grandTotal, invoice.currency)}
                      </td>
                      <td className="text-right font-medium">
                        {invoice.status === 'unpaid' && invoice.paidAmount > 0 ? (
                          <span className="text-green-600">
                            {formatCurrency(invoice.paidAmount, invoice.currency)}
                          </span>
                        ) : invoice.status === 'paid' ? (
                          <span className="text-green-600">
                            {formatCurrency(invoice.totals.grandTotal, invoice.currency)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td>
                        <div className="flex space-x-2">
                          <Link
                            to={`/invoices/${invoice._id}/edit`}
                            className="text-blue-600 hover:text-blue-500 text-sm"
                          >
                            {t('common.edit')}
                          </Link>
                          <button
                            onClick={() => pdfMutation.mutate(invoice._id)}
                            disabled={pdfMutation.isPending}
                            className="text-green-600 hover:text-green-500 text-sm"
                          >
                            {t('invoices.generatePdf')}
                          </button>
                          <button
                            onClick={() => handleSendMail(invoice)}
                            className="text-purple-600 hover:text-purple-500 text-sm"
                          >
                            {t('invoices.sendMail')}
                          </button>
                          {canDelete('invoices') && (
                            <button
                              onClick={() => {
                                Swal.fire({
                                  title: t('invoices.confirmDelete'),
                                  text: t('invoices.confirmDeleteText'),
                                  icon: 'warning',
                                  showCancelButton: true,
                                  confirmButtonColor: '#d33',
                                  cancelButtonColor: '#3085d6',
                                  confirmButtonText: t('invoices.yesDelete'),
                                  cancelButtonText: t('common.cancel')
                                }).then((result) => {
                                  if (result.isConfirmed) {
                                    deleteMutation.mutate(invoice._id);
                                  }
                                });
                              }}
                              disabled={deleteMutation.isPending}
                              className="text-red-600 hover:text-red-500 text-sm"
                            >
                              {t('common.delete')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-700">
                  {t('invoices.showing')} {((pagination.page - 1) * pagination.limit) + 1} {t('common.to')}{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} {t('common.of')}{' '}
                  {pagination.total} {t('invoices.results')}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="btn btn-secondary btn-sm"
                  >
                    {t('common.previous')}
                  </button>
                  <span className="flex items-center px-3 py-1 text-sm">
                    {t('common.page')} {pagination.page} {t('common.of')} {pagination.pages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                    className="btn btn-secondary btn-sm"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 text-6xl mb-4">📄</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('invoices.noInvoices')}</h3>
            <p className="text-gray-500 mb-4">{t('invoices.createFirst')}</p>
            <Link to="/invoices/new" className="btn btn-primary">
              {t('invoices.createInvoice')}
            </Link>
          </div>
        )}
      </div>

      {/* PDF Modal */}
      {pdfModal.isOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {t('invoices.pdfPreview')}
              </h3>
              <button
                onClick={closePdfModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="border rounded-lg overflow-hidden">
              <div className="w-full h-[600px] bg-gray-100 flex flex-col items-center justify-center">
                <object
                  data={`${pdfModal.url}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0`}
                  type="application/pdf"
                  className="w-full h-full border-0"
                  title={t('invoices.invoicePdf')}
                  style={{ width: '100%', height: '100%' }}
                >
                  <div className="text-center p-8">
                    <p className="text-gray-600 mb-4">{t('invoices.pdfCannotDisplay')}</p>
                    <a
                      href={pdfModal.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      {t('invoices.openPdfNewTab')}
                    </a>
                  </div>
                </object>
                <div className="mt-2 text-sm text-gray-600">
                  {t('invoices.pdfViewerNote')}
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                {t('common.file')}: {pdfModal.filename}
              </div>
              <div className="flex space-x-2">
                <a
                  href={pdfModal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary text-sm"
                >
                  {t('companies.openInNewTab')}
                </a>
                <button
                  onClick={downloadPdf}
                  className="btn btn-primary text-sm"
                >
                  {t('companies.download')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Mail Modal */}
      <SendMailModal
        isOpen={sendMailModal.isOpen}
        onClose={() => setSendMailModal({ isOpen: false, invoiceId: null, customerEmail: '', customerName: '' })}
        invoiceId={sendMailModal.invoiceId}
        customerEmail={sendMailModal.customerEmail}
        customerName={sendMailModal.customerName}
      />
    </div>
  );
};

export default Invoices;
